import Subtitle from '@/components/GlobalNewsletterModal/Subtitle'
import SanityImage from '@/components/SanityImage/SanityImage'
import { GlobalSettingsContext } from '@/context/GlobalSettings'
import { NEWSLETTER_MODAL_COOKIE_NAME, NEWSLETTER_MODAL_DEFAULT_EXPIRY_DAYS, REGIONS } from '@/data'
import useI18n from '@/hooks/use-i18n'
import { getCookie, setCookie } from '@/utils/storage'
import classnames from 'classnames'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useContext, useEffect, useRef, useState } from 'react'
import styles from './GlobalNewsletterModal.module.scss'
import Header from './Header'
import NewsletterForm from './NewsletterForm'

const SUCCESS_AUTO_DISMISS_DELAY = 5000 // 5 seconds

const LOCALE_PREFIX_REGEX = new RegExp(`^/(${Object.values(REGIONS).join('|')})/?`)

/**
 * Checks whether the current page is excluded from showing the newsletter modal.
 * Strips the locale prefix (e.g. /en-us/locations/arcadia → locations/arcadia)
 * then does an exact match against excluded slugs from Sanity.
 */
const isPathExcluded = (pathname: string | null, excludedSlugs?: string[]): boolean => {
  if (!pathname || !excludedSlugs?.length) return false

  const normalizedPath = pathname.replace(LOCALE_PREFIX_REGEX, '').replace(/^\//, '')

  return excludedSlugs.some(slug => {
    if (!slug) return false
    if (slug === '/') return normalizedPath === ''
    return normalizedPath === slug
  })
}

const GlobalNewsletterModal = () => {
  const { newsletterModalData } = useContext(GlobalSettingsContext)
  const { isEnabled, delayTime, dismissalDays, excludedSlugs, content } = newsletterModalData || {}
  const pathname = usePathname()
  const excludedSlugsKey = excludedSlugs?.join(',') ?? ''
  const { eyebrow, header, subtitle, backgroundImage, successMessage } = content || {}
  const hasBackgroundImage = !!backgroundImage?.asset
  const [isVisible, setIsVisible] = useState(false)
  const [formSuccess, setFormSuccess] = useState(false)
  const overlayRef = useRef<HTMLDivElement>(null)
  const { i18n } = useI18n()

  useEffect(() => {
    if (!isEnabled || getCookie(NEWSLETTER_MODAL_COOKIE_NAME) || isPathExcluded(pathname, excludedSlugs)) {
      return
    }

    // Show modal after delay
    const timer = setTimeout(
      () => {
        // Save scroll position before locking
        const scrollY = window.scrollY
        document.body.style.top = `-${scrollY}px`
        setIsVisible(true)
        document.body.classList.add('global-modal-open')
      },
      (delayTime || 0) * 1000,
    )

    return () => clearTimeout(timer)
  // excludedSlugs omitted — excludedSlugsKey is the serialized stable value
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, delayTime, pathname, excludedSlugsKey])

  useEffect(() => {
    // Auto-dismiss after showing success message
    if (formSuccess) {
      const timer = setTimeout(() => {
        handleDismiss()
      }, SUCCESS_AUTO_DISMISS_DELAY)

      return () => clearTimeout(timer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSuccess])

  // Enable wheel scrolling on the overlay
  useEffect(() => {
    if (!overlayRef.current || !isVisible) return

    const handleWheel = (e: WheelEvent) => {
      if (!overlayRef.current) return

      // Prevent the default scroll behavior
      e.preventDefault()

      // Manually scroll the overlay
      overlayRef.current.scrollTop += e.deltaY
    }

    const overlay = overlayRef.current
    overlay.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      overlay.removeEventListener('wheel', handleWheel)
    }
  }, [isVisible])

  const handleDismiss = () => {
    setIsVisible(false)
    document.body.classList.remove('global-modal-open')
    // Restore scroll position
    const scrollY = parseInt(document.body.style.top || '0', 10) * -1
    document.body.style.top = ''
    window.scrollTo(0, scrollY)
    setCookie({
      name: NEWSLETTER_MODAL_COOKIE_NAME,
      value: 'true',
      maxAge: (dismissalDays ?? NEWSLETTER_MODAL_DEFAULT_EXPIRY_DAYS) * 86400,
    })
  }

  const handleFormSuccess = () => {
    setFormSuccess(true)
  }

  if (!isEnabled || !isVisible || !content) {
    return null
  }

  return (
    <div
      ref={overlayRef}
      className={`${styles.overlay} overlay-scrollable`}
      onClick={e => e.target === e.currentTarget && handleDismiss()}
    >
      <div
        className={classnames(styles.modal, { [styles.modalWithImage]: hasBackgroundImage })}
        onClick={e => e.stopPropagation()}
      >
        {hasBackgroundImage && (
          <div className={styles.imageColumn}>
            <SanityImage
              image={backgroundImage}
              className={styles.modalImage}
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </div>
        )}

        {hasBackgroundImage && (
          <div className={styles.baoMascotOverlap}>
            <Image
              src="/images/baobao-mascot.svg"
              alt=""
              width={207}
              height={200}
              aria-hidden="true"
            />
          </div>
        )}

        <div className={styles.formColumn}>
          {!hasBackgroundImage && (
            <div className={styles.baoMascot}>
              <Image
                src="/images/baobao-background.png"
                alt=""
                width={336}
                height={317}
                aria-hidden="true"
              />
            </div>
          )}

          <button
            className={styles.closeButton}
            onClick={handleDismiss}
            aria-label={i18n('ariaCloseModal')}
          />

          <div className={styles.content}>
            {!formSuccess && (
              <>
                {eyebrow && <div className={styles.eyebrow}>{eyebrow}</div>}
                {header && <Header content={header} />}
                {subtitle && <Subtitle content={subtitle} />}
              </>
            )}

            <NewsletterForm
              onDismiss={handleFormSuccess}
              successMessage={{
                title: successMessage?.title || [],
                subtitle: successMessage?.subtitle || [],
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

GlobalNewsletterModal.displayName = 'GlobalNewsletterModal'

export default GlobalNewsletterModal
